import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEye,
  faPencil,
  faDesktop,
  faCalendar,
  faLocationDot,
  faPalette,
  faLayerGroup,
  faImage,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';

import { ROUTES } from '@/constants';
import { Paginator, Spinner } from '@/components/ui';
import { usePresentationsPage, PRESENTATION_KEYS, CONTENT_LIST_PAGE_SIZE } from '@/api';
import { StatusBadge, ctxStr, fmtDate, getSlideCount } from '@/components/ContentList';
import { GenerationProgress } from '@/components/GenerationProgress';
import styles from './ItemsList.module.css';

const PAGE_SIZE = CONTENT_LIST_PAGE_SIZE;

// ── Component ────────────────────────────────────────────

export function PresentationsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderTitle, setHeaderSlot } = useOutletContext<{
    setHeaderTitle: (title: string) => void;
    setHeaderSlot: (slot: React.ReactNode) => void;
  }>();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isFetching, isError } = usePresentationsPage(currentPage);

  useEffect(() => {
    if (isError) {
      toast.error('Error loading presentations. Please try again.');
    }
  }, [isError]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: PRESENTATION_KEYS.all('presentation') });
  }, [queryClient]);

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  useEffect(() => {
    const countStr = data ? ` (${data.count})` : '';
    setHeaderTitle(`Presentations${countStr}`);
    setHeaderSlot(
      <div className={styles.headerSlot}>
        <button className={styles.refreshBtn} onClick={handleRefresh} title="Refresh">
          <FontAwesomeIcon icon={faRotate} spin={isFetching} />
        </button>
        <button
          className={styles.newBtnHeader}
          onClick={() => navigate(ROUTES.dashboardGeneratePresentation)}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span className={styles.hideOnMobile}>Generate New</span>
        </button>
      </div>,
    );
    return () => {
      setHeaderTitle('');
      setHeaderSlot(null);
    };
  }, [setHeaderTitle, setHeaderSlot, data, navigate, isFetching, handleRefresh]);

  return (
    <div className={styles.page}>
      {/* ── List ── */}
      {isLoading ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : data?.results.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FontAwesomeIcon icon={faDesktop} />
          </div>
          <p className={styles.emptyTitle}>No presentations yet</p>
          <p className={styles.emptyText}>Generate your first AI-powered event proposal.</p>
          <button
            className={styles.emptyBtn}
            onClick={() => navigate(ROUTES.dashboardGeneratePresentation)}
          >
            <FontAwesomeIcon icon={faPlus} /> Generate Presentation
          </button>
        </div>
      ) : (
        <div className={styles.cardList}>
          {data?.results.map((item) => {
            const thumbnail = item.generated_images?.[0] ?? null;
            const eventType = ctxStr(item, 'eventType');
            const date = ctxStr(item, 'eventDate');
            const location = ctxStr(item, 'location');
            const theme = ctxStr(item, 'theme');
            const primary = ctxStr(item, 'primaryColor') || '#0C1B33';
            const accent = ctxStr(item, 'accentColor') || '#C9A84C';
            const slideCount = getSlideCount(item);
            const statusCls =
              (styles[`status_${item.status}` as keyof typeof styles] as string) ?? '';

            return (
              <div
                key={item.id}
                className={`${styles.card} ${statusCls}`}
                onClick={() =>
                  navigate(ROUTES.dashboardPresentationDetail.replace(':id', String(item.id)))
                }
              >
                {/* Thumbnail */}
                <div className={styles.presThumb}>
                  {thumbnail ? (
                    <img src={thumbnail} alt={item.name} className={styles.thumbImg} />
                  ) : (
                    <div
                      className={styles.thumbPlaceholder}
                      style={{
                        background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
                      }}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <h3 className={styles.cardName}>{item.name}</h3>
                    <StatusBadge
                      status={item.status}
                      since={item.created}
                      generationProgress={item.generation_progress}
                      styles={styles as Record<string, string>}
                    />
                  </div>

                  {(eventType || location) && (
                    <div className={styles.cardMeta}>
                      {eventType && <span className={styles.metaChip}>{eventType}</span>}
                      {location && (
                        <span className={styles.metaItem}>
                          <FontAwesomeIcon icon={faLocationDot} className={styles.metaIcon} />
                          {location}
                        </span>
                      )}
                    </div>
                  )}

                  {(date || slideCount > 0 || theme) && (
                    <div className={styles.cardDetails}>
                      {date && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faCalendar} className={styles.detailIcon} />
                          {fmtDate(date)}
                        </span>
                      )}
                      {slideCount > 0 && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faLayerGroup} className={styles.detailIcon} />
                          {slideCount} {slideCount === 1 ? 'slide' : 'slides'}
                        </span>
                      )}
                      {theme && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faPalette} className={styles.detailIcon} />
                          {theme}
                        </span>
                      )}
                    </div>
                  )}

                  <div className={styles.cardFooter}>
                    Created {fmtDate(item.created)}
                    {item.created_by_email && (
                      <span className={styles.cardCreatedBy}>by {item.created_by_email}</span>
                    )}
                  </div>
                </div>

                {/* Mini progress stepper — between body and actions */}
                {(item.status === 'generating' || item.status === 'wizard_draft') && item.generation_progress && (
                  <div className={styles.miniProgressBar}>
                    <GenerationProgress progress={item.generation_progress} mode="mini" />
                  </div>
                )}

                {/* Actions */}
                <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                    onClick={() =>
                      navigate(ROUTES.dashboardPresentationDetail.replace(':id', String(item.id)))
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> View
                  </button>
                  <button
                    className={styles.actionBtn}
                    onClick={() =>
                      navigate(ROUTES.dashboardPresentationEdit.replace(':id', String(item.id)))
                    }
                  >
                    <FontAwesomeIcon icon={faPencil} /> Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className={styles.paginatorWrap}>
          <Paginator page={currentPage} numPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
}
